// bluaIQ — Gantt Cascade Utility
// When a task's dates change, cascade the delta to all dependent tasks

import type { GanttTask } from './ganttDemoData'

/** Shift a date string by N calendar days */
function shiftDate(dateStr: string, days: number): string {
  const d = new Date(dateStr)
  d.setDate(d.getDate() + days)
  return d.toISOString().slice(0, 10)
}

/** Get the duration in days between two date strings */
function durationDays(start: string, end: string): number {
  return Math.round((new Date(end).getTime() - new Date(start).getTime()) / 86_400_000)
}

/**
 * Cascade date changes through a task list.
 * When a task's end date moves, every downstream dependent task
 * shifts by the same delta (preserving its own duration).
 */
export function cascadeDates(
  tasks: GanttTask[],
  changedId: string,
  newStart: string,
  newEnd: string,
): GanttTask[] {
  const taskMap = new Map(tasks.map(t => [t.id, { ...t }]))

  const original = tasks.find(t => t.id === changedId)
  if (!original) return tasks

  const changed = taskMap.get(changedId)!
  const endDelta = durationDays(original.endDate, newEnd)
  changed.startDate = newStart
  changed.endDate = newEnd

  // BFS through dependents
  const queue = [changedId]
  const visited = new Set<string>([changedId])

  while (queue.length > 0) {
    const parentId = queue.shift()!
    const parent = taskMap.get(parentId)!

    for (const [id, task] of taskMap) {
      if (visited.has(id)) continue
      if (!task.dependencies.includes(parentId)) continue

      // This task depends on parent — check if it needs to shift
      const parentEnd = new Date(parent.endDate)
      const taskStart = new Date(task.startDate)

      // Only shift if the parent now ends after the task was supposed to start
      // (finish-to-start dependency)
      if (parentEnd >= taskStart || endDelta > 0) {
        const dur = durationDays(task.startDate, task.endDate)
        // Start day after parent ends (skip weekend)
        const rawNewStart = shiftDate(parent.endDate, 3) // next Monday heuristic
        task.startDate = rawNewStart
        task.endDate = shiftDate(rawNewStart, dur)
      }

      visited.add(id)
      queue.push(id)
    }
  }

  return Array.from(taskMap.values())
}

/**
 * Find the critical path — longest sequence from first to last task.
 * Returns the set of task IDs on the critical path.
 */
export function findCriticalPath(tasks: GanttTask[]): Set<string> {
  // fix(gantt): guard empty tasks array — on first render for real projects
  // activeTasks is [] before the API resolves, causing tasks[0].id to throw
  // "TypeError: Cannot read properties of undefined (reading 'id')".
  // Returning an empty Set lets the component reach the activeTasks.length === 0
  // check in the JSX and render the loading/empty state correctly.
  if (tasks.length === 0) return new Set<string>()

  const taskMap = new Map(tasks.map(t => [t.id, t]))

  // Topological sort via in-degree
  const inDegree = new Map<string, number>()
  const adj = new Map<string, string[]>()
  for (const t of tasks) {
    inDegree.set(t.id, t.dependencies.length)
    if (!adj.has(t.id)) adj.set(t.id, [])
    for (const dep of t.dependencies) {
      if (!adj.has(dep)) adj.set(dep, [])
      adj.get(dep)!.push(t.id)
    }
  }

  // Earliest finish times
  const ef = new Map<string, number>()
  const pred = new Map<string, string | null>()
  const queue: string[] = []

  for (const t of tasks) {
    const end = new Date(t.endDate).getTime()
    ef.set(t.id, end)
    pred.set(t.id, null)
    if (t.dependencies.length === 0) queue.push(t.id)
  }

  while (queue.length > 0) {
    const id = queue.shift()!
    const parentEf = ef.get(id)!
    for (const child of adj.get(id) || []) {
      if (parentEf > ef.get(child)!) {
        ef.set(child, parentEf + durationDays(taskMap.get(child)!.startDate, taskMap.get(child)!.endDate) * 86_400_000)
        pred.set(child, id)
      }
      inDegree.set(child, inDegree.get(child)! - 1)
      if (inDegree.get(child) === 0) queue.push(child)
    }
  }

  // Find last finishing task and trace back
  let lastId = tasks[0].id
  let maxEf = 0
  for (const [id, val] of ef) {
    if (val > maxEf) { maxEf = val; lastId = id }
  }

  const critical = new Set<string>()
  let cur: string | null = lastId
  while (cur) {
    critical.add(cur)
    cur = pred.get(cur) || null
  }

  // Also include tasks explicitly marked as critical in demo
  for (const t of tasks) {
    if (t.isCritical) critical.add(t.id)
  }

  return critical
}
